import Controller, { inject as controller } from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import checkType from 'garaje/utils/check-file-type';
import { inject as service } from '@ember/service';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { action } from '@ember/object';
import { alias, and, equal, filterBy } from 'macro-decorators';

export default class EmployeesImportController extends Controller {
  @service state;
  @service flashMessages;
  @service featureFlags;
  @service employeeUpload;
  @service workplaceMetrics;

  @controller('employees.directory') employeesController;

  @alias('state.vrSubscription') vrSubscription;
  @alias('state.roomsSubscription') roomsSubscription;
  @alias('state.desksSubscription') desksSubscription;
  @alias('state.deliveriesSubscription') deliveriesSubscription;
  @alias('state.workplaceSubscription') workplaceSubscription;
  @alias('state.currentCompany') currentCompany;
  @and('vrSubscription', 'vrSubscription.canAccessAssistants') canAccessAssistants;
  @equal('state.currentLocation.employeesCsvUploadStatus', 'in_progress') isServerProcessing;

  @tracked isProcessing = false;
  @tracked parsedObjects = null;
  @tracked originalFile = null;
  @tracked isActive;
  maxPreviewLength = 500;

  get cannotImport() {
    const subscribedToDesks = this.desksSubscription && !this.desksSubscription.isBasicPlan;
    const subscribedToRooms = this.roomsSubscription && !this.roomsSubscription.isBasicPlan;
    const subscribedToDeliveries = this.deliveriesSubscription && !this.deliveriesSubscription.isBasicPlan;
    const subscribedToWorkplace = this.workplaceSubscription && !this.workplaceSubscription.isBasicPlan;
    const subscribedToVisitors =
      this.vrSubscription &&
      !this.vrSubscription.isBasicPlan &&
      this.vrSubscription.plan &&
      !this.vrSubscription.plan.includes('standard');

    const payingCustomer =
      subscribedToDesks || subscribedToRooms || subscribedToDeliveries || subscribedToWorkplace || subscribedToVisitors;

    // only gate if gating feature flag is enabled
    if (!this.featureFlags.isEnabled('employee-feature-gating')) return false;

    return !payingCustomer && !this.model.isConnectedToProperty && this.parsedObjects?.length > 50;
  }

  get csvFields() {
    const fields = [
      {
        label: 'name',
        color: '#2211a6',
        value: 'Full Name',
        isRequired: true,
      },
      {
        label: 'email',
        color: '#aa1151',
        value: 'alice@example.com',
        isRequired: true,
      },
      {
        label: 'phone number',
        color: '#117700',
        value: '(555) 555-5555',
        isRequired: false,
      },
    ];

    if (this.canAccessAssistants) {
      fields.push({
        label: 'assistant emails',
        color: '#aa1151',
        value: '"bob@example.com, eve@example.com"',
        isRequired: false,
      });
    }

    fields.push({
      label: 'department',
      color: '#2211a6',
      value: 'Human Resources',
      isRequired: false,
    });

    return fields;
  }

  @filterBy('csvFields', 'isRequired', false) optionalCsvFields;

  @action
  async uploadCsvToServer() {
    try {
      this.workplaceMetrics.trackEvent('EMPLOYEE_DIRECTORY_SELECT_CSV_FILE_BUTTON_CLICKED');
      await this.employeeUpload.uploadEmployeesCSV(this.originalFile);
      await this.employeesController.resetEmployeesTask.perform();

      this.parsedObjects = null;

      this.flashMessages.showAndHideFlash('success', 'Saved!');

      if (this.isActive) {
        this.transitionToRoute('employees');
      }
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  }

  @action
  toggleProcessing() {
    this.isProcessing = !this.isProcessing;
  }

  @action
  handleCsvResults(contents, originalFile) {
    this.workplaceMetrics.trackEvent('EMPLOYEE_DIRECTORY_IMPORT_CSV_FILE_BUTTON_CLICKED');
    this.originalFile = originalFile;
    const checkResult = checkType(originalFile, ['csv']);

    if (checkResult.isValid) {
      const formattedArray = [];
      contents.data.forEach(function (datum) {
        let object;
        // passing in a CSV without a header row is legacy behavior. department upload is
        // only supported for CSVs with a header row.
        if (Array.isArray(datum)) {
          // do not extend the array mapping below to include department. this is on
          // purpose.
          object = { name: datum[0], email: datum[1], phone: datum[2], assistant: datum[3] };
        } else {
          object = {
            name: datum.name,
            email: datum.email,
            phone: datum.phone_number,
            assistant: datum.assistant_emails,
            department: datum.department,
          };
        }
        formattedArray.pushObject(object);
      });
      this.parsedObjects = formattedArray;
    } else {
      const text = `Only files with extensions .csv are allowed. You uploaded a file with extension ${checkResult.extension}.`;
      this.flashMessages.showFlash('error', text);
    }
  }
}
